/**
 * Renders one demo-scene clip. Mounted in a Sequence per clip in the DOM
 * layer above the Three.js compositor (demo scenes are synthetic DOM, not
 * video textures). The backdrop is keyed off the clip's global timeline
 * position so it reads as one continuous space across scene cuts; scene
 * content fades in/out over a few frames at the clip edges.
 *
 * Scenes are authored in a fixed 1920x1080 design space and scaled to fit
 * the composition; the backdrop fills the real frame so non-16:9 exports
 * letterbox gracefully.
 */
import { AbsoluteFill, interpolate, useCurrentFrame, useVideoConfig } from 'remotion'
import { demoPalette } from './theme'
import { Backdrop } from './ui'
import { BrowseScene, OutroScene, TiltScene, TitleScene } from './scenes'
import { DEMO_STAGE_H, DEMO_STAGE_W, type DemoScenePayload, type DemoStyle } from './types'

export function DemoSceneRenderer({
  scene,
  demoStyle,
  durationInFrames,
  globalStartSec,
}: {
  scene: DemoScenePayload
  demoStyle?: DemoStyle
  durationInFrames: number
  globalStartSec: number
}) {
  const frame = useCurrentFrame()
  const { width, height } = useVideoConfig()
  const pal = demoPalette(demoStyle)
  const stageScale = Math.min(width / DEMO_STAGE_W, height / DEMO_STAGE_H)
  const contentOpacity = interpolate(
    frame,
    [0, 7, Math.max(8, durationInFrames - 6), Math.max(9, durationInFrames - 1)],
    [0, 1, 1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  )

  return (
    <AbsoluteFill>
      <Backdrop pal={pal} timeOffsetSec={globalStartSec} />
      <AbsoluteFill style={{ alignItems: 'center', justifyContent: 'center', opacity: contentOpacity }}>
        <div
          style={{
            width: DEMO_STAGE_W,
            height: DEMO_STAGE_H,
            transform: `scale(${stageScale})`,
            transformOrigin: 'center center',
            position: 'relative',
            flexShrink: 0,
          }}
        >
          {scene.kind === 'title' ? <TitleScene scene={scene} pal={pal} /> : null}
          {scene.kind === 'browse' ? <BrowseScene scene={scene} pal={pal} /> : null}
          {scene.kind === 'tilt' ? <TiltScene scene={scene} pal={pal} /> : null}
          {scene.kind === 'outro' ? <OutroScene scene={scene} pal={pal} /> : null}
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  )
}
